import { useTranslation } from 'react-i18next'

/**
 * Sparkline — kleine Inline-SVG-Trendlinie fuer Listenzeilen (z.B. Hotspot-
 * Uptime, Invoice-Betraege). Reine Darstellung, keine Interaktion.
 *
 * Props:
 * - values: number[]       Zahlenreihe (min. 2 Werte fuer eine Linie)
 * - color?: string         CSS-Farbe/Token, z.B. "var(--primary)" (Default)
 * - width?: number         SVG-Breite in px (Default 100)
 * - height?: number        SVG-Hoehe in px (Default 36, passt zu .spark)
 * - fill?: boolean         Flaeche unter der Linie einfaerben (Default true)
 * - ariaLabel?: string     Eigenes aria-label; sonst generischer Fallback
 */
type SparklineProps = {
  values: number[]
  color?: string
  width?: number
  height?: number
  fill?: boolean
  ariaLabel?: string
}

export function Sparkline({
  values,
  color = 'var(--primary)',
  width = 100,
  height = 36,
  fill = true,
  ariaLabel,
}: SparklineProps) {
  const { t } = useTranslation('components')

  if (!values || values.length < 2) return null

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const pad = 2

  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (width - pad * 2)
    const y = height - pad - ((v - min) / range) * (height - pad * 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const line = points.join(' ')

  return (
    <svg
      className="spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel ?? t('sparkline.ariaLabel')}
    >
      {fill && (
        <polygon
          points={`${pad},${height - pad} ${line} ${width - pad},${height - pad}`}
          fill={color}
          fillOpacity={0.14}
        />
      )}
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
